import { ChangeEvent, ReactElement, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { fetchEventPatch } from '../../services/event.service';
import { ROUTES } from '../../helpers/constans';

type TEditableEvent = {
  name?: string;
  slogan?: string;
  link?: string;
  sponsorPrice?: number;
  sponsorCurrency?: string;
};

const EventsEdit = (): ReactElement => {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const location = useLocation();
  const currentEvent = (location.state as { event?: TEditableEvent } | null)?.event || null;

  const [form, setForm] = useState<TEditableEvent>({
    name: currentEvent?.name || '',
    slogan: currentEvent?.slogan || '',
    link: currentEvent?.link || '',
    sponsorPrice: Number(currentEvent?.sponsorPrice || 0),
    sponsorCurrency: currentEvent?.sponsorCurrency || 'GBP',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const isReady = useMemo(() => !!id, [id]);

  const handleChange =
    (key: keyof TEditableEvent) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setForm((prev) => ({
        ...prev,
        [key]: key === 'sponsorPrice' ? Number(value || 0) : value,
      }));
    };

  const handleSave = async () => {
    if (!id) {
      setError('Missing event id.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await fetchEventPatch(id, {
        name: form.name || '',
        slogan: form.slogan || '',
        link: form.link || '',
        sponsorPrice: Number(form.sponsorPrice || 0),
        sponsorCurrency: form.sponsorCurrency || 'GBP',
      });

      navigate(ROUTES.organisations);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save event changes.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isReady) {
    return <Alert severity="error">Missing event identifier.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <Paper elevation={0} variant="outlined" sx={{ width: '100%', maxWidth: 820, p: 3, borderRadius: '16px' }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#17324d' }}>Edit event</Typography>
            <Typography sx={{ fontSize: '14px', color: '#6b7c8f', mt: 0.5 }}>
              Update xdos-owned event metadata. qdos will only receive the approved projection fields.
            </Typography>
          </Box>

          {!currentEvent ? (
            <Alert severity="warning">
              This editor works best when opened from the organisation event panel. The event id is present, but the
              current values were not passed into the page state.
            </Alert>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField label="Event name" value={form.name || ''} onChange={handleChange('name')} fullWidth />
          <TextField label="Slogan" value={form.slogan || ''} onChange={handleChange('slogan')} fullWidth />
          <TextField label="Link" value={form.link || ''} onChange={handleChange('link')} fullWidth />
          <TextField
            label="Sponsor price"
            type="number"
            value={form.sponsorPrice ?? 0}
            onChange={handleChange('sponsorPrice')}
            fullWidth
          />
          <TextField
            label="Sponsor currency"
            value={form.sponsorCurrency || 'GBP'}
            onChange={handleChange('sponsorCurrency')}
            fullWidth
          />

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate(-1)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default EventsEdit;
